import React from "react";
import { useTenant } from "@/context/TenantContext";

const TRAILER_LABELS = ["Reefer", "Dry Van", "Conestoga", "Flatbed", "Step Deck"];

export function AuthLayout({ eyebrow, title, subtitle, children, footer }) {
    const { tenant } = useTenant();
    const displayName = tenant?.display_name || "RentFlow";
    const logoUrl = tenant?.logo_url || "/favicon.png";
    return (
        <div className="min-h-screen flex bg-white">
            {/* Form side */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10 lg:p-16">
                <div className="w-full max-w-md fade-up">
                    {eyebrow && (
                        <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                            {eyebrow}
                        </p>
                    )}
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 text-balance">
                        {title}
                    </h1>
                    {subtitle && (
                        <p className="mt-3 text-base text-slate-600 leading-relaxed">{subtitle}</p>
                    )}

                    <div className="mt-8">{children}</div>

                    {footer && <div className="mt-8 text-sm text-slate-600">{footer}</div>}
                </div>
            </div>

            {/* Image side */}
            <div
                data-testid="auth-hero"
                className="hidden lg:flex w-1/2 relative overflow-hidden bg-slate-200"
            >

                {/* Top brand block */}
                <div className="absolute top-0 left-0 right-0 px-10 lg:px-14 pt-8 lg:pt-10 z-10 flex items-center gap-3">
                    <img
                        src={logoUrl}
                        alt={displayName}
                        data-testid="auth-tenant-logo"
                        className="h-8 w-auto max-w-[140px] object-contain rounded-md"
                    />
                    <div className="leading-tight">
                        <div
                            data-testid="auth-tenant-name"
                            className="font-display font-bold text-base tracking-tight text-slate-900"
                        >
                            {displayName}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-blue-900/80 -mt-0.5">
                            Customer portal
                        </div>
                    </div>
                </div>

                {/* Trailer column — flush on the gradient, no card or shadow */}
                <div className="relative z-10 flex-1 flex items-center justify-center px-4 lg:px-6 pt-20 lg:pt-24 pb-16 lg:pb-20">
                    <div className="relative h-full w-auto flex items-center justify-center">
                        <img
                            src="/rentflow-trailers.png"
                            alt="RentFlow supported trailer types: reefer, dry van, conestoga, flatbed, step deck"
                            className="h-full max-h-[88vh] w-auto object-contain"
                        />
                        {/* Evenly-spaced label rail to the right of the trailers */}
                        <div className="absolute inset-y-0 -right-24 xl:-right-28 2xl:-right-32 hidden xl:flex flex-col justify-around py-[6%]">
                            {TRAILER_LABELS.map((label) => (
                                <span
                                    key={label}
                                    className="text-[10px] uppercase tracking-[0.22em] text-slate-700 font-semibold whitespace-nowrap"
                                >
                                    {label}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Bottom value prop */}
                <div className="absolute bottom-0 left-0 right-0 px-10 lg:px-14 pb-8 lg:pb-10 z-10">
                    <p className="font-display text-base lg:text-lg text-slate-700 max-w-md leading-snug text-balance">
                        Onboard equipment renters in minutes.
                    </p>
                </div>
            </div>
        </div>
    );
}
